/**
 * Controller for Knowledge Sources — data fetching and mutations.
 * Talks to the gateway via knowledge.sources.* RPC methods.
 */

import type { GatewayBrowserClient } from "../gateway.ts";

export type KnowledgeSourceUI = {
  id: string;
  name: string;
  type: "web-portal" | "api" | "auto";
  enabled: boolean;
  /** Natural-language description of the data source (AI analyzes this) */
  instructions?: string;
  url?: string;
  username?: string;
  password?: string;
  paths?: string[];
  endpoint?: string;
  apiKey?: string;
  method?: "GET" | "POST";
  headers?: Record<string, string>;
  body?: string;
  responseFilter?: string;
  intervalMinutes: number;
  cronExpression?: string;
  lastCollectedAt?: string;
  lastStatus: "idle" | "collecting" | "success" | "error";
  lastError?: string;
  collectionCount: number;
  documentsIngested: number;
  createdAt: string;
  updatedAt: string;
};

export type KnowledgeSourcesState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  knowledgeSourcesLoading: boolean;
  knowledgeSourcesError: string | null;
  knowledgeSources: KnowledgeSourceUI[];
  knowledgeSourceEditing: KnowledgeSourceUI | null;
  knowledgeSourceFormOpen: boolean;
  knowledgeSourceCollecting: string | null;
};

export async function loadKnowledgeSources(state: KnowledgeSourcesState): Promise<void> {
  if (!state.client || !state.connected) return;
  if (state.knowledgeSourcesLoading) return;
  state.knowledgeSourcesLoading = true;
  state.knowledgeSourcesError = null;
  try {
    const res = (await state.client.request("knowledge.sources.list", {})) as {
      sources: KnowledgeSourceUI[];
    };
    state.knowledgeSources = res.sources ?? [];
  } catch (err) {
    state.knowledgeSourcesError = String(err);
  } finally {
    state.knowledgeSourcesLoading = false;
  }
}

export async function createKnowledgeSource(
  state: KnowledgeSourcesState,
  input: Partial<KnowledgeSourceUI>,
): Promise<boolean> {
  if (!state.client || !state.connected) return false;
  try {
    await state.client.request("knowledge.sources.create", input);
    await loadKnowledgeSources(state);
    return true;
  } catch (err) {
    state.knowledgeSourcesError = String(err);
    return false;
  }
}

export async function updateKnowledgeSource(
  state: KnowledgeSourcesState,
  id: string,
  patch: Partial<KnowledgeSourceUI>,
): Promise<boolean> {
  if (!state.client || !state.connected) return false;
  try {
    await state.client.request("knowledge.sources.update", { id, ...patch });
    await loadKnowledgeSources(state);
    return true;
  } catch (err) {
    state.knowledgeSourcesError = String(err);
    return false;
  }
}

export async function deleteKnowledgeSource(
  state: KnowledgeSourcesState,
  id: string,
): Promise<boolean> {
  if (!state.client || !state.connected) return false;
  try {
    await state.client.request("knowledge.sources.delete", { id });
    await loadKnowledgeSources(state);
    return true;
  } catch (err) {
    state.knowledgeSourcesError = String(err);
    return false;
  }
}

export async function collectKnowledgeSource(
  state: KnowledgeSourcesState,
  id: string,
): Promise<void> {
  if (!state.client || !state.connected) return;
  state.knowledgeSourceCollecting = id;
  try {
    await state.client.request("knowledge.sources.collect", { id });
    await loadKnowledgeSources(state);
  } catch (err) {
    state.knowledgeSourcesError = String(err);
  } finally {
    state.knowledgeSourceCollecting = null;
  }
}
